import { AnyDecoder, ObjectData, VersionBoxDecoder } from "@simonbackx/simple-encoding";
import { Formatter } from "@stamhoofd/utility";
import { Version } from "../Version";
import { Filter, FilterDecoder, FilterDefinition } from "./FilterDefinition";
/**
 * This is a list of filters, but without the known definitions because we can only decode at a point where the definitions are known.
 * This allows for more recursive structures in filter definitions.
 */
export class FilterGroupEncoded {
    constructor(data, version) {
        this.data = data;
        this.version = version;
    }
    decode(definitions) {
        const decoder = new FilterGroupDecoder(definitions);
        const versionBoxDecoder = new VersionBoxDecoder(decoder);
        return versionBoxDecoder.decode(new ObjectData({
            data: this.data,
            version: this.version
        }, { version: 0 })).data;
    }
    encode(context) {
        if (context.version < 169) {
            return this.data;
        }
        return {
            data: this.data,
            version: this.version
        };
    }
    static encode(filter) {
        return new FilterGroupEncoded(filter.encode({ version: Version }), Version);
    }
    static decode(data) {
        if (data.optionalField("version")) {
            const d = data.field("data").decode(AnyDecoder);
            const version = data.field("version").integer;
            return new FilterGroupEncoded(d, version);
        }
        const d = data.decode(AnyDecoder);
        const version = data.context.version;
        return new FilterGroupEncoded(d, version);
    }
}
export var GroupFilterMode;
(function (GroupFilterMode) {
    GroupFilterMode["Or"] = "Or";
    GroupFilterMode["And"] = "And";
    /**
     * Means !(A || B) == !A && !B
     */
    GroupFilterMode["Nor"] = "Nor";
    /**
     * Means !(A && B) == !A || !B
     */
    GroupFilterMode["Nand"] = "Nand";
})(GroupFilterMode || (GroupFilterMode = {}));
/**
 * A filter is an encodebale structure, that is associated with a specific definition
 */
export class FilterGroup extends Filter {
    constructor(definitions, filters = [], mode = GroupFilterMode.And) {
        super();
        this.filters = [];
        this.mode = GroupFilterMode.And;
        this.definitions = definitions;
        this.filters = filters;
        this.definition = new FilterGroupDecoder(this.definitions);
        this.mode = mode;
    }
    get encoded() {
        return FilterGroupEncoded.encode(this);
    }
    setDefinitions(definitions) {
        this.definitions = definitions;
        // Make sure that decoding is also updated
        this.definition.definitions = this.definitions;
    }
    getDefinitions() {
        return this.definitions;
    }
    doesMatch(object) {
        for (const filter of this.filters) {
            if (!filter.doesMatch(object)) {
                if (this.mode === GroupFilterMode.And) {
                    return false;
                }
                if (this.mode === GroupFilterMode.Nand) {
                    return true;
                }
            }
            else {
                if (this.mode === GroupFilterMode.Or) {
                    return true;
                }
                if (this.mode === GroupFilterMode.Nor) {
                    return false;
                }
            }
        }
        return this.filters.length == 0 || this.mode === GroupFilterMode.And || this.mode === GroupFilterMode.Nor;
    }
    encode(context) {
        return {
            filters: this.filters.map(f => f.encode(context)),
            mode: this.mode,
            definitionId: this.definition.id
        };
    }
    get inverted() {
        const group = this.clone();
        switch (group.mode) {
            case GroupFilterMode.And:
                group.mode = GroupFilterMode.Nand;
                break;
            case GroupFilterMode.Nand:
                group.mode = GroupFilterMode.And;
                break;
            case GroupFilterMode.Or:
                group.mode = GroupFilterMode.Nor;
                break;
            case GroupFilterMode.Nor:
                group.mode = GroupFilterMode.Or;
                break;
        }
        return group;
    }
    toString() {
        switch (this.mode) {
            case GroupFilterMode.Or:
                return Formatter.joinLast(this.filters.map(f => f.toString()), ", ", " of ");
            case GroupFilterMode.And:
                return Formatter.joinLast(this.filters.map(f => f.toString()), ", ", " en ");
            case GroupFilterMode.Nand:
                return Formatter.joinLast(this.filters.map(f => f.inverted.toString()), ", ", " of ");
            case GroupFilterMode.Nor:
                return Formatter.joinLast(this.filters.map(f => f.inverted.toString()), ", ", " en ");
        }
    }
}
class FailableDecoder {
    constructor(decoder) {
        this.decoder = decoder;
    }
    decode(data) {
        try {
            return {
                value: this.decoder.decode(data)
            };
        }
        catch (e) {
            return {
                error: e
            };
        }
    }
}
export class FilterGroupDecoder extends FilterDefinition {
    constructor(definitions) {
        super({
            id: "filter_group",
            name: "Filtergroep",
            description: 'Een groep van filters die je samen als filter kan gebruiken, waardoor je complexe filters kan opstellen (bv. door en/of te combineren).',
            getValue: () => {
                throw new Error("Can't get value of group filter definition");
            }
        });
        this.definitions = definitions;
    }
    decode(data) {
        var _a, _b;
        const filterDecoder = new FilterDecoder([...this.definitions, this]);
        const group = this.createFilter();
        const failableDecoder = new FailableDecoder(filterDecoder);
        const decodedFilters = data.field("filters").array(failableDecoder);
        const errors = decodedFilters.flatMap(f => f.error ? [f.error] : []);
        if (errors.length > 0) {
            console.warn('Failed to decode FilterGroup completely', errors);
        }
        group.filters = decodedFilters.flatMap(f => f.value ? [f.value] : []);
        group.mode = (_b = (_a = data.optionalField("mode")) === null || _a === void 0 ? void 0 : _a.enum(GroupFilterMode)) !== null && _b !== void 0 ? _b : GroupFilterMode.And;
        return group;
    }
    createFilter() {
        return new FilterGroup(this.definitions);
    }
}
//# sourceMappingURL=FilterGroup.js.map